import { t } from "i18next";
import { concat, mergeDeepLeft, uniq } from "ramda";
import { UAParser } from "ua-parser-js";

import {
  BROWSER_UPGRADE_PAGE_LINKS,
  DEFAULT_MINIMUM_SUPPORTED_BROWSER_VERSIONS,
  UNSUPPORTED_BROWSERS,
} from "./constants";

const convertToPlatformBrowserName = browserName =>
  browserName.replace(/\s/g, "");

const getRequiredMinBrowserVersion = (
  browserName,
  supportedBrowserVersionsMap
) => supportedBrowserVersionsMap[convertToPlatformBrowserName(browserName)];

const convertSemverToFloat = browserVersion => {
  const [majorVersion, minorVersion] = browserVersion.split(".");

  return parseFloat(`${majorVersion}.${minorVersion}`) || "";
};

const isVersionSupported = (browserVersion, requiredMinBrowserVersion) => {
  const isBrowserFullySupported =
    browserVersion >= requiredMinBrowserVersion?.softLimit;

  const isBrowserPartiallySupported =
    browserVersion >= requiredMinBrowserVersion?.hardLimit;

  return { isBrowserFullySupported, isBrowserPartiallySupported };
};

const buildDownloadPageLink = browserName =>
  BROWSER_UPGRADE_PAGE_LINKS[convertToPlatformBrowserName(browserName)];

const isBrowserUnsupported = (browserName, unsupportedBrowsersList) =>
  unsupportedBrowsersList.includes(convertToPlatformBrowserName(browserName));

const isBrowserSupported = (browserName, supportedBrowserVersionsMap) =>
  !!supportedBrowserVersionsMap[convertToPlatformBrowserName(browserName)];

export const getBrowserStatus = (overrides, additionalUnsupported) => {
  const { browser, device } = new UAParser(navigator.userAgent).getResult();

  const browserName =
    browser.name || t("neetoMolecules.browserSupport.unknownBrowserMsg");
  const browserVersion = convertSemverToFloat(browser.version || "");
  const browserType = browser.type;
  const deviceType = device.type;

  const supportedBrowserVersionsMap = mergeDeepLeft(
    overrides,
    DEFAULT_MINIMUM_SUPPORTED_BROWSER_VERSIONS
  );

  const unsupportedBrowsersList = uniq(
    concat(UNSUPPORTED_BROWSERS, additionalUnsupported)
  );

  const isUnsupportedBrowser = isBrowserUnsupported(
    browserName,
    unsupportedBrowsersList
  );

  const isKnownBrowser = isBrowserSupported(
    browserName,
    supportedBrowserVersionsMap
  );

  const requiredMinBrowserVersion = isKnownBrowser
    ? getRequiredMinBrowserVersion(browserName, supportedBrowserVersionsMap)
    : null;

  const { isBrowserFullySupported, isBrowserPartiallySupported } =
    isKnownBrowser
      ? isVersionSupported(browserVersion, requiredMinBrowserVersion)
      : { isBrowserFullySupported: false, isBrowserPartiallySupported: false };

  const downloadPageLink = isKnownBrowser
    ? buildDownloadPageLink(browserName)
    : null;

  return {
    browserName,
    browserVersion,
    browserType,
    deviceType,
    isUnsupportedBrowser,
    isKnownBrowser,
    requiredMinBrowserVersion,
    isBrowserFullySupported,
    isBrowserPartiallySupported,
    downloadPageLink,
  };
};
